// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what playing a Khipu does, without the DOM (the Khipu plan,
 * slice 1; `docs/AYNI_XR_KHIPU_REPLACES_TOURS_PLAN.md`).
 *
 * A Khipu plays one of two ways:
 * - **Its path** (stories, briefings, a record's steps): each step puts its
 *   dataset, camera (or region), date, playback, layers, colours and pins on
 *   the globe, unless the step leaves that field `open` to the person playing
 *   it, and shows its captions, questions, quiz and media. A step moves on
 *   when the person says, or after `advance.seconds` while the Khipu is
 *   playing.
 * - **Its log** (a `session` record): the events are replayed in order,
 *   with the waits of the meeting shortened to between 1.5 and 8 seconds,
 *   so the record keeps its rhythm without its silences.
 *
 * A lesson is a canvas and never plays this way (`lessonPlayerUI`).
 *
 * Written from scratch for AYNI (owner, 2026-10-05): nothing here comes
 * from TerraViz's code.
 */
import type { Khipu, KhipuDatasetRef, KhipuEvent, KhipuMedia, KhipuStep, KhipuText, KhipuTime, KhipuView, KhipuEffects, KhipuTransition } from './khipu'
import type { KhipuSatellites } from './khipu'
import { sanitiseKhipuSatellites } from './satellites/khipuSatellites'
import { stepHold } from './studio/film'
import { capOverlays } from './mapLayers'
import { localized } from './khipu'
import { MAX_PINS } from './measure/pins'
import { PALETTE_IDS, type PaletteId } from './colorScaleDisplay'
import { FOLLOW, parseTimeRule, type GlobeTimeRule } from './globeSlots'

/** An overlay a step puts on: a layer from `/api/layers`, in its colour if the step says one. */
export interface StepOverlayPlan {
  layerId: string
  tint?: 'white' | 'black' | 'source'
}

/** What a step puts on the globe and shows beside it. */
export interface StepPlan {
  /** The dataset key to show; null for the bare Earth; undefined: left as it is. */
  datasetKey: string | null | undefined
  view: KhipuView | null
  /** The box to frame when the step sets no camera (DSA geoBounds). */
  region: StepRegion | null
  /**
   * The date (`at`, or `relative` to the first or latest frame), and how the
   * dataset plays: the step's `time`, filled in from its DSA `playback`
   * settings (speed, autoplay, start offset) where `time` says nothing.
   */
  time: StepTime | null
  /** Palette, inversion and colour limits (physical values); null: as they are. */
  display: StepDisplay | null
  /** Pins to show (an empty list clears them); null: left as they are. */
  pins: StepPin[] | null
  /** A multiple-choice question with its answer, to reveal when asked. */
  quiz: StepQuiz | null
  /** The globe spinning or still, and how fast (a multiple of the default); null: as it is. */
  autorotate: { on: boolean; speed?: number } | null
  /** Overlays to put on (`layer:<id>` datasets), when the step sets its layers; the first two (`MAX_OVERLAYS`). */
  overlays: StepOverlayPlan[] | null
  /** Real-time streams to play over the dataset (`stream:` datasets, as `R2_DASH_<id>`), the first lowest, when the step sets its layers. */
  realtime: string[] | null
  /** AYNI (owner, 2026-10-12): each of those streams' opacity and eye, when the step sets its layers. */
  realtimeLook: StepRtLook[] | null
  /**
   * AYNI (multi-globe plan): the other globes the step shows beside its
   * own — each its app dataset id (null: bare Earth) and time rule — with
   * the layout; null when the step says nothing of them.
   */
  globes: { layout: '1' | '2h' | '2v' | '4'; others: StepGlobePlan[] } | null
  /** Captions, questions and tasks for everyone watching, in order. */
  captions: Array<{ kind: string; text: string }>
  /** Notes for the person presenting. */
  notes: string[]
  media: Array<{ kind: KhipuMedia['kind']; url: string; title: string; caption: string; credit?: string }>
  /** Seconds before it moves on by itself while playing; null: it waits. */
  advanceSeconds: number | null
  /**
   * Khipu 1.1: how the camera arrives (a step with effects or a hold but no
   * transition glides in over 1.2 s, as steps always did), how long the step
   * holds, and its effects; null for a step without any of them, which moves
   * as before.
   */
  motion: { transition: KhipuTransition; hold: number; effects: KhipuEffects | null } | null
  /** Khipu 1.2: the Satellites layer the step sets; null: left as it is. */
  satellites: KhipuSatellites | null
}

/** One of the other globes of a step: its dataset key (null: bare Earth; the player resolves it as the step's own) and its time rule. */
export interface StepGlobePlan {
  datasetKey: string | null
  time: GlobeTimeRule
}

/** AYNI (multi-globe plan): the step's globes, when it names any; a bad rule falls back to the same date. */
function globesOf(step: KhipuStep): StepPlan['globes'] {
  const raw = Array.isArray(step.globes) ? step.globes : null
  const layoutRaw = step.layout
  if (!raw && !layoutRaw) return null
  const others: StepGlobePlan[] = (raw ?? []).slice(0, 3).flatMap((g): StepGlobePlan[] => {
    const o = obj(g)
    if (!o) return []
    const datasetKey = typeof o.dataset === 'string' && o.dataset ? o.dataset : null
    const time = (typeof o.time === 'string' ? parseTimeRule(o.time) : null) ?? FOLLOW
    return [{ datasetKey, time }]
  })
  const layout = layoutRaw === '1' || layoutRaw === '2h' || layoutRaw === '2v' || layoutRaw === '4'
    ? layoutRaw
    : others.length === 0 ? '1' : others.length === 1 ? '2h' : '4'
  return { layout, others }
}

export interface StepRegion {
  west: number
  south: number
  east: number
  north: number
}

export interface StepTime extends KhipuTime {
  /** Seconds into the dataset's video, when no date is given. */
  offsetSeconds?: number
}

export interface StepDisplay {
  palette?: PaletteId
  invert?: boolean
  /** Colour limits in the dataset's units: the palette's first and last colours. */
  min?: number
  max?: number
  /** AYNI (owner, 2026-10-12): the dataset's opacity, 0 … 1, and its eye; absent: as they are. */
  opacity?: number
  hidden?: boolean
}

/** AYNI (owner, 2026-10-12): a real-time overlay's opacity and eye, as a step sets them (absent: opaque and shown). */
export interface StepRtLook {
  /** The app's id (`R2_DASH_<id>`). */
  id: string
  opacity: number
  hidden: boolean
}

export interface StepPin {
  lat: number
  lon: number
  note: string
  /** Index into the six pin colours. */
  color: number
}

export interface StepQuiz {
  question: string
  options: string[]
  correctIndex: number
  hint: string
  difficulty: string
}

const LAYER_PREFIX = 'layer:'
const STREAM_PREFIX = 'R2_DASH_'
const TINTS = new Set(['white', 'black', 'source'])

/** Whether the step decides `field`, rather than leaving it to the person playing. */
const sets = (step: KhipuStep, field: string): boolean => !(step.open ?? []).includes(field)

export function planStep(k: Khipu, step: KhipuStep, lang = 'en'): StepPlan {
  const layers = Array.isArray(step.layers) ? step.layers : null
  const overlays = layers && sets(step, 'layers')
    ? capOverlays(layers.flatMap((l): StepOverlayPlan[] => {
        if (l.role !== 'overlay') return []
        const ref = k.datasets.find((d) => d.key === l.dataset)
        if (!ref || !ref.ref.startsWith(LAYER_PREFIX)) return []
        return [{ layerId: ref.ref.slice(LAYER_PREFIX.length), ...(l.tint && TINTS.has(l.tint) ? { tint: l.tint } : {}) }]
      }))
    : null
  const realtimeLayers = layers && sets(step, 'layers')
    ? layers.flatMap((l) => {
        if (l.role !== 'realtime') return []
        const ref = k.datasets.find((d) => d.key === l.dataset)
        return ref && ref.ref.startsWith('stream:') && ref.dsaId && l.dataset !== step.dataset ? [{ id: STREAM_PREFIX + ref.dsaId, layer: l }] : []
      })
    : null
  const realtime = realtimeLayers ? [...new Set(realtimeLayers.map((r) => r.id))] : null
  const realtimeLook = realtimeLayers
    ? realtime!.map((id): StepRtLook => {
        const l = realtimeLayers.find((r) => r.id === id)!.layer
        return { id, opacity: num(l.opacity) ? Math.min(1, Math.max(0, l.opacity)) : 1, hidden: l.hidden === true }
      })
    : null
  const prompts = step.prompts ?? []
  const forAll = prompts.filter((p) => (p.audience ?? 'everyone') !== 'presenter' && p.kind !== 'note' && p.kind !== 'rationale')
  const seconds = step.advance?.mode === 'timer' && typeof step.advance.seconds === 'number' && step.advance.seconds >= 1
    ? step.advance.seconds
    : null
  const view = sets(step, 'view') ? step.view ?? null : null
  return {
    datasetKey: sets(step, 'dataset') ? step.dataset : undefined,
    view,
    region: !view && sets(step, 'region') ? regionOf(step.region) : null,
    time: sets(step, 'time') ? timeOf(step) : null,
    display: sets(step, 'display') ? displayOf(step.display) : null,
    pins: sets(step, 'pins') ? pinsOf(step.pins, lang) : null,
    quiz: quizOf(step.quiz, lang),
    // The spin goes with the camera: a step that leaves the view open leaves it too.
    autorotate: sets(step, 'view') ? autorotateOf(step) : null,
    overlays,
    realtime,
    realtimeLook,
    satellites: step.satellites === null ? { on: false } : sanitiseKhipuSatellites(step.satellites),
    globes: sets(step, 'globes') ? globesOf(step) : null,
    captions: forAll.map((p) => ({ kind: p.kind, text: localized(p.text, lang) })).filter((c) => c.text),
    notes: prompts.filter((p) => !forAll.includes(p)).map((p) => localized(p.text, lang)).filter(Boolean),
    media: (step.media ?? []).map((m) => ({
      kind: m.kind,
      url: m.url,
      title: localized(m.title, lang),
      caption: localized(m.caption, lang),
      ...(m.credit ? { credit: m.credit } : {}),
    })),
    advanceSeconds: seconds,
    motion: (step.transition !== undefined || step.effects !== undefined || step.hold !== undefined) && sets(step, 'view')
      ? { transition: step.transition ?? { move: 'glide', seconds: 1.2 }, hold: stepHold(step), effects: step.effects ?? null }
      : null,
  }
}

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null)

function regionOf(raw: unknown): StepRegion | null {
  const r = obj(raw)
  if (!r || !num(r.west) || !num(r.south) || !num(r.east) || !num(r.north)) return null
  if (Math.abs(r.south) > 90 || Math.abs(r.north) > 90 || r.south >= r.north) return null
  if (Math.abs(r.west) > 180 || Math.abs(r.east) > 180) return null
  return { west: r.west, south: r.south, east: r.east, north: r.north }
}

/** The step's `time`, with its DSA `playback` settings filling what `time` leaves unsaid. */
function timeOf(step: KhipuStep): StepTime | null {
  const time = obj(step.time)
  const pb = obj(step.playback)
  const out: StepTime = {}
  if (time) {
    if (typeof time.at === 'string' && time.at) out.at = time.at
    else if (time.relative === 'latest' || time.relative === 'first') out.relative = time.relative
    if (typeof time.playing === 'boolean') out.playing = time.playing
    if (num(time.rate) && time.rate > 0) out.rate = time.rate
  }
  if (pb) {
    if (out.rate === undefined && num(pb.defaultSpeed) && pb.defaultSpeed > 0) out.rate = pb.defaultSpeed
    if (out.playing === undefined && typeof pb.autoplay === 'boolean') out.playing = pb.autoplay
    if (!out.at && !out.relative && num(pb.startOffset) && pb.startOffset > 0) out.offsetSeconds = pb.startOffset
  }
  return Object.keys(out).length ? out : null
}

/** The step's `autorotate`, else its DSA `playback.defaultAutorotate`. */
function autorotateOf(step: KhipuStep): { on: boolean; speed?: number } | null {
  const own = obj(step.autorotate)
  if (own && typeof own.on === 'boolean') {
    return own.on && num(own.speed) && own.speed >= 0.25 && own.speed <= 4 ? { on: true, speed: own.speed } : { on: own.on }
  }
  const pb = obj(step.playback)
  return pb && typeof pb.defaultAutorotate === 'boolean' ? { on: pb.defaultAutorotate } : null
}

function displayOf(raw: unknown): StepDisplay | null {
  const d = obj(raw)
  if (!d) return null
  const out: StepDisplay = {}
  if (typeof d.palette === 'string' && (PALETTE_IDS as readonly string[]).includes(d.palette)) out.palette = d.palette as PaletteId
  if (typeof d.invert === 'boolean') out.invert = d.invert
  // Limits come as a pair, low below high, or not at all: one alone, or a
  // reversed pair, would draw one flat colour.
  if (num(d.min) && num(d.max) && d.max > d.min) {
    out.min = d.min
    out.max = d.max
  }
  // AYNI (owner, 2026-10-12): the dataset's opacity and eye.
  if (num(d.opacity)) out.opacity = Math.min(1, Math.max(0, d.opacity))
  if (typeof d.hidden === 'boolean') out.hidden = d.hidden
  return Object.keys(out).length ? out : null
}

/** Most pins a step shows (the schema's limit, and the six pin colours; four until 2026-10-09). */
export const STEP_MAX_PINS = MAX_PINS

function pinsOf(raw: unknown, lang: string): StepPin[] {
  if (!Array.isArray(raw)) return []
  const pins = raw
    .flatMap((p): StepPin[] => {
      const o = obj(p)
      if (!o || !num(o.lat) || !num(o.lon) || Math.abs(o.lat) > 90 || Math.abs(o.lon) > 180) return []
      const color = Number.isInteger(o.color) && (o.color as number) >= 0 && (o.color as number) < STEP_MAX_PINS ? (o.color as number) : -1
      return [{ lat: o.lat, lon: o.lon, note: textOf(o.note, lang), color }]
    })
    .slice(0, STEP_MAX_PINS)
  // A pin without a colour takes one no other pin has: the step's pins are told apart by it.
  const taken = new Set(pins.filter((p) => p.color >= 0).map((p) => p.color))
  for (const p of pins) {
    if (p.color >= 0) continue
    p.color = Array.from({ length: STEP_MAX_PINS }, (_, c) => c).find((c) => !taken.has(c)) ?? 0
    taken.add(p.color)
  }
  return pins
}

function quizOf(raw: unknown, lang: string): StepQuiz | null {
  const q = obj(raw)
  if (!q || !Array.isArray(q.options)) return null
  const question = textOf(q.question, lang)
  const options = q.options.map((o) => textOf(o, lang))
  if (!question || options.length < 2 || options.length > 4 || options.some((o) => !o)) return null
  const correct = q.correctIndex
  if (!Number.isInteger(correct) || (correct as number) < 0 || (correct as number) >= options.length) return null
  return {
    question,
    options,
    correctIndex: correct as number,
    hint: textOf(q.hint, lang),
    difficulty: typeof q.difficulty === 'string' ? q.difficulty : '',
  }
}

/** Whether any step moves on by itself, so the player offers Play and Pause. */
export function hasTimedSteps(k: Khipu): boolean {
  return k.path.steps.some((s) => s.advance?.mode === 'timer' && typeof s.advance.seconds === 'number' && s.advance.seconds >= 1)
}

/** A session record with events plays its log; everything else its path. */
export function playsLog(k: Khipu): boolean {
  return k.kind === 'session' && (k.log?.events.length ?? 0) > 0
}

/** Shortest and longest wait between two replayed events, in ms. */
export const REPLAY_MIN_GAP_MS = 1500
export const REPLAY_MAX_GAP_MS = 8000

/** The events a replay shows, in order, with the shortened wait before each. */
export interface ReplayMoment {
  /** Ms to wait after the previous moment (0 for the first). */
  waitMs: number
  event: KhipuEvent
}

/** The event types a replay acts on; the rest stay in the record for reading. */
export const REPLAYED_EVENTS: ReadonlySet<string> = new Set([
  'dataset', 'view', 'time', 'layers', 'note', 'activity.start', 'activity.end', 'answers', 'pin.add', 'pin.remove', 'step',
])

export function replayMoments(events: readonly KhipuEvent[]): ReplayMoment[] {
  const kept = events.filter((e) => REPLAYED_EVENTS.has(e.type)).slice().sort((a, b) => a.t - b.t)
  return kept.map((event, i) => ({
    waitMs: i === 0 ? 0 : Math.min(REPLAY_MAX_GAP_MS, Math.max(REPLAY_MIN_GAP_MS, event.t - kept[i - 1].t)),
    event,
  }))
}

/** What the globe and the caption line look like after the moments up to `index`, applied in order. */
export interface ReplayState {
  dataset: string | null | undefined
  view: KhipuView | null
  time: KhipuTime | null
  /** AYNI (2026-10-09): the overlays then (two at most); undefined until a layers event says. */
  overlays: StepOverlayPlan[] | undefined
  caption: string
  pins: Array<{ id: string; lat: number; lon: number; note?: string }>
}

/**
 * The state after replaying moments 0..index: what a jump on the scrubber
 * puts on the globe at once, rather than replaying every event before it.
 */
export function replayStateAt(moments: readonly ReplayMoment[], index: number, lang = 'en', datasets: readonly KhipuDatasetRef[] = []): ReplayState {
  const state: ReplayState = { dataset: undefined, view: null, time: null, overlays: undefined, caption: '', pins: [] }
  for (const { event } of moments.slice(0, index + 1)) {
    const d = event.data ?? {}
    switch (event.type) {
      case 'dataset':
        // The dataset's key in the Khipu's datasets; null for the bare Earth.
        state.dataset = typeof d.dataset === 'string' ? d.dataset : null
        break
      case 'view':
        if (num(d.lat) && num(d.lon) && num(d.zoom)) state.view = { lat: d.lat, lon: d.lon, zoom: d.zoom, ...(d.flat === true ? { flat: true } : {}) }
        break
      case 'time':
        if (typeof d.at === 'string') state.time = { at: d.at }
        break
      case 'layers': {
        // In the step's own shape (`layers`, keys into the Khipu's datasets),
        // as the recorder writes it; or layer ids (`overlays`).
        const fromStep = Array.isArray(d.layers)
          ? d.layers.flatMap((l): StepOverlayPlan[] => {
              const o = obj(l)
              if (!o || o.role !== 'overlay' || typeof o.dataset !== 'string') return []
              const ref = datasets.find((r) => r.key === o.dataset)
              if (!ref || !ref.ref.startsWith(LAYER_PREFIX)) return []
              return [{ layerId: ref.ref.slice(LAYER_PREFIX.length), ...(typeof o.tint === 'string' && TINTS.has(o.tint) ? { tint: o.tint as StepOverlayPlan['tint'] } : {}) }]
            })
          : null
        const ids = Array.isArray(d.overlays) ? d.overlays.filter((o): o is string => typeof o === 'string').map((layerId) => ({ layerId })) : []
        state.overlays = capOverlays(fromStep ?? ids)
        break
      }
      case 'note':
      case 'activity.start':
        state.caption = textOf(d.text ?? d.prompt, lang)
        break
      case 'activity.end':
        state.caption = ''
        break
      case 'answers':
        state.caption = answersLine(d)
        break
      case 'pin.add':
        if (num(d.lat) && num(d.lon)) {
          state.pins.push({ id: typeof d.id === 'string' ? d.id : `p${state.pins.length + 1}`, lat: d.lat, lon: d.lon, ...(typeof d.note === 'string' && d.note ? { note: d.note } : {}) })
        }
        break
      case 'pin.remove':
        state.pins = state.pins.filter((p) => p.id !== d.id)
        break
    }
  }
  return state
}

function textOf(v: unknown, lang: string): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return localized(v as KhipuText, lang)
  return ''
}

/** A question's totals as one line: "Yes: 12 · No: 3". */
function answersLine(d: Record<string, unknown>): string {
  const totals = d.totals
  if (!totals || typeof totals !== 'object') return ''
  return Object.entries(totals as Record<string, unknown>)
    .filter(([, n]) => typeof n === 'number')
    .map(([option, n]) => `${option}: ${n}`)
    .join(' · ')
}

/**
 * A pausable countdown for a step that moves on by itself. `pause` keeps
 * what is left; `resume` runs it out; `start` begins a new one. Time and
 * timers are parameters, so it is tested without waiting.
 */
export interface AdvanceClock {
  start(ms: number): void
  pause(): void
  resume(): void
  cancel(): void
  /** Ms left, or null when nothing is counting down or held. */
  remaining(): number | null
}

export function createAdvanceClock(
  onDone: () => void,
  timers: { now: () => number; set: (fn: () => void, ms: number) => unknown; clear: (handle: unknown) => void } = {
    now: () => Date.now(),
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  },
): AdvanceClock {
  let handle: unknown = null
  let endsAt: number | null = null
  let held: number | null = null
  const stop = () => {
    if (handle !== null) timers.clear(handle)
    handle = null
  }
  const run = (ms: number) => {
    stop()
    held = null
    endsAt = timers.now() + ms
    handle = timers.set(() => {
      handle = null
      endsAt = null
      onDone()
    }, ms)
  }
  return {
    start: (ms) => run(Math.max(0, ms)),
    pause: () => {
      if (endsAt === null) return
      held = Math.max(0, endsAt - timers.now())
      endsAt = null
      stop()
    },
    resume: () => {
      if (held !== null) run(held)
    },
    cancel: () => {
      stop()
      endsAt = null
      held = null
    },
    remaining: () => (endsAt !== null ? Math.max(0, endsAt - timers.now()) : held),
  }
}
